import { motion } from "framer-motion";
import { Music, Mic2, PartyPopper, Users } from "lucide-react";

const cards = [
    {
        id: "bands",
        title: "Live Bands",
        blurb: "Headline acts, DJs and a sound system that goes all night.",
        video: "https://res.cloudinary.com/dyssxsxce/video/upload/q_auto/f_auto/v1777418151/band_xzxolz.mp4",
        icon: Music,
        accent: "bg-[#0D9488] text-white",
    },
    {
        id: "speakers",
        title: "Guest Speakers",
        blurb: "Inspiring talks from founders & creators in between sets.",
        image: "https://res.cloudinary.com/dyssxsxce/image/upload/q_auto/f_auto/v1777334988/WhatsApp_Image_2026-04-27_at_12.37.05_4_tequ0r.jpg",
        icon: Mic2,
        accent: "bg-[#FACC15] text-slate-900",
    },
    {
        id: "entertainment",
        title: "Entertainment",
        blurb: "Games, surprises & moments you'll talk about for months.",
        image: "https://res.cloudinary.com/dyssxsxce/image/upload/q_auto/f_auto/v1777334988/WhatsApp_Image_2026-04-27_at_12.37.05_yraj2r.jpg",
        icon: PartyPopper,
        accent: "bg-[#FF5E3A] text-white",
    },
    {
        id: "networking",
        title: "Networking",
        blurb: "Meet your people — drinks in hand, warm summer breeze.",
        image: "https://res.cloudinary.com/dyssxsxce/image/upload/q_auto/f_auto/v1777334988/WhatsApp_Image_2026-04-27_at_12.37.04_hs02yh.jpg",
        icon: Users,
        accent: "bg-slate-900 text-white",
    },
];

const featuredBands = [
    {
        name: "Noughts & Crosses",
        image: "https://res.cloudinary.com/dyssxsxce/image/upload/q_auto/f_auto/v1779276090/81a466b3-02c1-4abe-9396-f4ea25237f4f.png",
    },
    {
        name: "The Strip Lights",
        image: "https://res.cloudinary.com/dyssxsxce/image/upload/q_auto/f_auto/v1779276078/e6527ff1-3875-4f2b-ab00-1033e35569a2.png",
    },
    {
        name: "Jamie Mcardle",
        image: "https://res.cloudinary.com/dyssxsxce/image/upload/q_auto/f_auto/v1779276069/2d63e7f9-a766-4124-877a-25b11ef7c28f.png",
    },
    {
        name: "NO Reply",
        description: "Rock and pop cover band",
        image: "https://res.cloudinary.com/dyssxsxce/image/upload/q_auto/f_auto/v1779276058/bf283e4d-1047-4217-8466-f67a9b94de34.png",
    }
];

export default function WhatToExpect() {
    return (
        <section
            id="expect"
            data-testid="what-to-expect"
            className="relative bg-[#FAF9F6] py-20 sm:py-28"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="max-w-3xl mb-12 sm:mb-16"
                >
                    <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#0D9488] mb-4">
                        What to expect
                    </p>
                    <h2 className="font-montserrat font-black tracking-tighter text-4xl sm:text-5xl md:text-6xl leading-[0.95] text-slate-900">
                        <span className="ais-gradient">A full day &amp; night</span>{" "}
                        of <span className="ais-underline">good stuff</span>.
                    </h2>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {cards.map((c, idx) => {
                        const Icon = c.icon;
                        return (
                            <motion.div
                                key={c.id}
                                data-testid={`expect-card-${c.id}`}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{
                                    duration: 0.6,
                                    delay: idx * 0.08,
                                }}
                                whileHover={{ y: -6 }}
                                className={`group relative flex flex-col overflow-hidden rounded-3xl bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-shadow duration-300 h-full min-h-[450px] lg:min-h-[500px]`}
                            >
                                {(c.image || c.video) && (
                                    <div className={`aspect-[4/3] md:aspect-auto overflow-hidden flex-1`}>
                                        {c.video ? (
                                            <video
                                                src={c.video}
                                                autoPlay
                                                muted
                                                loop
                                                playsInline
                                                controls
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                                            />
                                        ) : (
                                            <img
                                                src={c.image}
                                                alt={c.title}
                                                loading="lazy"
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                                            />
                                        )}
                                    </div>
                                )}
                                <div className="p-4 sm:p-5">
                                    <div
                                        className={`inline-flex items-center justify-center w-10 h-10 rounded-xl mb-3 ${c.accent}`}
                                    >
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <h3 className="font-display font-bold text-xl sm:text-2xl text-slate-900 mb-1">
                                        {c.title}
                                    </h3>
                                    <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                                        {c.blurb}
                                    </p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                <div className="mt-24">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12 sm:mb-16"
                    >
                        <h3 className="font-montserrat font-black tracking-tighter text-3xl sm:text-4xl md:text-5xl leading-[0.95] text-slate-900">
                            Featured <span className="text-[#FF5E3A]">Bands</span>
                        </h3>
                    </motion.div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
                        {featuredBands.map((band, idx) => (
                            <motion.div
                                key={band.name}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{
                                    duration: 0.6,
                                    delay: idx * 0.1,
                                }}
                                whileHover={{ y: -6 }}
                                className="group relative flex flex-col items-center text-center"
                            >
                                <div className="w-full aspect-square mb-4 overflow-hidden rounded-full shadow-lg border-4 border-white">
                                    <img 
                                        src={band.image} 
                                        alt={band.name}
                                        loading="lazy"
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out" 
                                    />
                                </div>
                                <h4 className="font-display font-bold text-lg sm:text-xl text-slate-900 mb-1">
                                    {band.name}
                                </h4>
                                {band.description && (
                                    <p className="text-slate-600 text-xs sm:text-sm">
                                        {band.description}
                                    </p>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
